import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { warnDev } from "@/lib/log";
import { useAgencyWS } from "@/hooks/useAgencyWS";
import { Clock, CheckCircle2, Users } from "lucide-react";

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m < 1) return `${s}s`;
  if (m < 60) return `${m} min ${s.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${(m % 60).toString().padStart(2, "0")}`;
}

export function CounterStats({ agencyId }) {
  const [stats, setStats] = useState(null);

  const refresh = useCallback(async () => {
    if (!agencyId) return;
    try {
      const { data } = await api.get("/stats/overview", { params: { agency_id: agencyId } });
      setStats(data);
    } catch (e) {
      warnDev("counter stats fetch failed", e);
    }
  }, [agencyId]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  useAgencyWS(agencyId, useCallback((msg) => {
    if (msg.type === "ticket.update" || msg.type === "ticket.called" || msg.type === "ticket.created") {
      refresh();
    }
  }, [refresh]));

  return (
    <div className="grid grid-cols-3 gap-3" data-testid="counter-stats">
      <StatCard
        icon={<Users className="w-5 h-5" />}
        label="En file (jour)"
        value={stats?.total ?? "—"}
        accent="text-slate-700"
        testid="stat-total"
      />
      <StatCard
        icon={<CheckCircle2 className="w-5 h-5" />}
        label="Traités aujourd'hui"
        value={stats?.completed ?? "—"}
        accent="text-[#16A34A]"
        testid="stat-completed"
      />
      <StatCard
        icon={<Clock className="w-5 h-5" />}
        label="Attente moyenne"
        value={formatDuration(stats?.avg_wait_seconds)}
        accent="text-[#0EA5E9]"
        testid="stat-avg-wait"
      />
    </div>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className={`flex items-center gap-2 ${accent}`}>
        {icon}
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      </div>
      <div className="mt-1 font-display font-black text-2xl text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
